import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faPlus,
  faPaw,
  faDollarSign,
  faBan,
  faClock,
  faMoneyBillAlt,
  faExclamationCircle,
  faQuestionCircle,
  faRedo,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../../api/api";
import { CURRENCY_CODE, DELIVERY_STATUS } from "../../config/constants";
import DeliveryDetailModal from "./partials/DeliveryDetails";
import CancelDelivery from "./partials/CancelDelivery";
import Pagination from "../../components/Pagination";
import { capitalizeWords, routes } from "../../config/utility";
import LoadingLogo from "../../components/Loading";
import DeliveryPaymentMethodSelection from "./partials/DeliveryPaymentMethod";
import { formatDistanceToNow } from "date-fns";
import DeliveryRequest from "./partials/DeliveryRequest";
import { Link } from "react-router-dom";

const PetOwnerDelivery = ({ user, role, permission, updateContext }) => {
  const [deliveries, setDeliveries] = useState([]);
  const [isShowModalOpen, setIsShowModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [isCancelDeliveryModalOpen, setIsCancelDeliveryModalOpen] = useState(false);
  const [isDeliveryPaymentMethodModalOpen, setIsDeliveryPaymentMethodModalOpen] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [limit, setLimit] = useState(0);


  const [partners, setPartners] = useState([]);
  const [ownerPets, setOwnerPets] = useState([]);
  const [tools, setTools] = useState([]);
  const [meta, setMeta] = useState({
    total: 0,
    per_page: 6,
    current_page: 1,
    last_page: 1,
    from: 1,
    to: 1,
  });
  const [editDelivery, setEditDelivery] = useState(null);

  useEffect(() => {
    fetchDeliveries();
  }, [statusFilter, currentPage]);

  const fetchDeliveries = async () => {
    setLoading(true);
    try {
      const response = await api.get("/deliveries", {
        params: {
          search: searchTerm,
          status: statusFilter,
          page: currentPage,
          per_page: meta.per_page,
        },
      });
      setDeliveries(response.data);
      setMeta(response.meta);
      setOwnerPets(response.pets);
      setPartners(response.partners);
      setTools(response.tools);
      setLimit(response.limit);
      setLoading(false);
    } catch (error) {
      toast.error("Failed to fetch delivery history.");
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditDelivery(null);
    setIsModalOpen(true);
  };

  const handleReorder = (delivery) => {
    // Check limit before allowing reorder
    if (limit === 3) {
      toast.error(
        "You have reached the max limit for 3 taxis at a time. Either complete or cancel a taxi to request another."
      );
      return;
    }

    // Create a reorder object with the delivery data formatted for the form
    const reorderData = {
      pickup_latitude: delivery.pickup_location?.latitude || null,
      pickup_longitude: delivery.pickup_location?.longitude || null,
      pickup_address: delivery.pickup_location?.address || "",
      dropoff_latitude: delivery.dropoff_location?.latitude || null,
      dropoff_longitude: delivery.dropoff_location?.longitude || null,
      dropoff_address: delivery.dropoff_location?.address || "",
      pet_ids: delivery.pets?.map(pet => pet.id) || [],
      want_cage: delivery.want_cage || false,
      two_way: delivery.two_way || false,
      scheduled_at: false, // Always set to instant for reorder
      scheduled_time: null,
      emergency_no: delivery.emergency_no || "",
      promo_code: "", // Reset promo code for reorder
      partner_id: delivery.partner_id || null,
      day: "",
      deliverySlot: null,
      slot_token: null,
      scheduleType: "instant",
      termsAccepted: false,
      services: ["taxi"],

      // Add reorder flag to indicate this is a reorder
      isReorder: true,
      originalDeliveryId: delivery.id
    };

    setEditDelivery(reorderData);
    setIsModalOpen(true);
  };

  // const handleEdit = (delivery) => {
  //   setEditDelivery(delivery);
  //   setIsModalOpen(true);
  // };

  const handleShow = (delivery) => {
    setEditDelivery(delivery);
    setIsShowModalOpen(true);
  };

  const handleSave = () => {
    setIsModalOpen(false);
    fetchDeliveries();
  };

  const handleNextPage = () => {
    if (meta.current_page < meta.last_page) {
      setCurrentPage(meta.current_page + 1);
    }
  };

  const handlePrevPage = () => {
    if (meta.current_page > 1) {
      setCurrentPage(meta.current_page - 1);
    }
  };

  const handleCancelDelivery = (delivery) => {
    setEditDelivery(delivery); // delivery
    setIsCancelDeliveryModalOpen(true);
  };

  const handleCancel = () => {
    setIsCancelDeliveryModalOpen(false);
    fetchDeliveries();
  };

  const handleShowModalClose = (shouldFetchDeliveries) => {
    setIsShowModalOpen(false);
    if (shouldFetchDeliveries) {
      fetchDeliveries(); // Re-fetch deliveries if true
    }
  };

  const handleCancelDeliveryPaymentMethod = () => {
    setIsDeliveryPaymentMethodModalOpen(false);
    fetchDeliveries();
  };

  const statusColorMap = {
    requested: "bg-purple-200 text-purple-800",
    finding_driver: "bg-purple-200 text-purple-800",
    driver_assigned: "bg-purple-800 text-purple-200",
    heading_to_pickup: "bg-purple-800 text-purple-200",
    arrived_at_pickup: "bg-purple-800 text-purple-200",
    en_route_to_dropoff: "bg-purple-800 text-purple-200",
    arrived_at_dropoff: "bg-purple-800 text-purple-200",
    en_route_to_return: "bg-purple-800 text-purple-200",
    arrived_at_return: "bg-purple-800 text-purple-200",
    completed: "bg-green-200 text-green-800",
    cancelled: "bg-red-200 text-red-800",
  };


  const renderPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= meta.last_page; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-3 py-1 rounded-full ${i === meta.current_page
            ? "bg-primary text-white"
            : "bg-white text-gray-700"
            } hover:bg-primary hover:text-white mx-1`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  const handleDeliveryPaymentMethod = (delivery) => {
    setEditDelivery(delivery); // delivery
    setIsDeliveryPaymentMethodModalOpen(true);
  };


  if (loading) {
    return <LoadingLogo />;
  }

  return (
    <div className="mx-auto  ">

      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-primary text-2xl sm:text-3xl font-bold">Paw Taxi</h1>
          <Link
            to={`${routes.guide}?type=taxi`}
            className="group flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-purple-100 hover:bg-purple-200 transition-all duration-300"
            title="View Walk Guide"
          >
            <FontAwesomeIcon
              icon={faQuestionCircle}
              className="text-primary text-lg sm:text-xl group-hover:scale-110 transition-transform duration-300"
            />
          </Link>
        </div>
        <button
          onClick={() => {
            if (limit === 3) {
              toast.warning(
                "You have reached the max limit for 3 taxis at a time. Either complete or cancel a taxi to request another.",
                { autoClose: 5000 }
              );
              return;
            }
            handleAdd();
          }}
          disabled={limit === 3}
          className={`w-full sm:w-auto bg-primary text-white px-4 py-2 rounded-lg flex items-center justify-center transition-all duration-300
      ${limit === 3 ? "opacity-60 cursor-not-allowed" : "hover:bg-accent"}`}
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Request New Taxi
        </button>
      </div>


      {/* <div className="flex space-x-4 mb-6">
        <div className="flex items-center border border-gray-300 rounded-lg p-2 shadow-md transition-all duration-300 hover:shadow-lg focus-within:shadow-2xl focus-within:bg-bodyMain">
          <FontAwesomeIcon
            icon={faSearch}
            className="text-gray-400 mr-2 transition-transform duration-300 hover:scale-110"
          />
          <input
            type="text"
            placeholder="Search Deliverys"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                fetchDeliveries();
              }
            }}
            className="outline-none focus:ring-0 bg-transparent w-full text-gray-700 placeholder-gray-400 focus:placeholder-gray-500 focus:text-black"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border rounded-lg p-2 shadow-sm"
        >
          <option value="all">All Deliverys</option>
          <option value="pending">Pending</option>
          <option value="completed">Completed</option>
          <option value="canceled">Canceled</option>
        </select>
      </div> */}



      <div className="flex flex-col space-y-4 mb-6">
        {/* More Filters Button */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="w-full sm:w-auto text-purple-700 font-semibold hover:text-purple-900 transition-all duration-300 py-2 px-4 rounded-lg hover:bg-purple-50"
          >
            {showAdvancedFilters ? "🔼 Hide Filters" : "🔽 More Filters"}
          </button>

          {/* Primary Walk Filters */}
          <div className="flex flex-wrap gap-2 justify-center w-full sm:w-auto">
            {["all", "ongoing", "scheduled"].map((filter) => (
              <label
                key={filter}
                className={`flex-1 sm:flex-initial flex items-center justify-center space-x-2 cursor-pointer px-4 sm:px-6 py-2 sm:py-3 rounded-full transition-all duration-300 shadow-md 
                  ${statusFilter === filter
                    ? "bg-white text-tblue shadow-lg scale-105"
                    : "bg-tblue text-white hover:scale-105"}`}
              >
                <input
                  type="radio"
                  name="walkStatus"
                  value={filter}
                  checked={statusFilter === filter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="hidden"
                />
                <span className="text-base sm:text-lg">
                  {filter === "all" ? "🐾" : filter === "ongoing" ? "🚶‍♂️" : "📅"}
                </span>
                <span className="text-xs sm:text-sm font-semibold capitalize">{filter}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Advanced Filters */}
        <div className={`transition-all duration-500 overflow-hidden ${showAdvancedFilters ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`}>
          <div className="p-2 sm:p-5">
            <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
              {Object.entries(DELIVERY_STATUS).map(([key, value]) => (
                <label
                  key={key}
                  className={`flex-1 sm:flex-initial min-w-[150px] flex items-center justify-center space-x-2 cursor-pointer px-3 sm:px-6 py-2 sm:py-3 rounded-full transition-all duration-300 shadow-md 
                    ${statusFilter === value
                      ? "bg-white text-tblue shadow-lg scale-105"
                      : "bg-tblue text-white hover:scale-105"}`}
                >
                  <input
                    type="radio"
                    name="walkStatus"
                    value={value}
                    checked={statusFilter === value}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="hidden"
                  />
                  <span className="text-base sm:text-lg">🐶</span>
                  <span className="text-xs sm:text-sm font-medium">{capitalizeWords(value)}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6 mt-8">
        {deliveries.length > 0 ? (
          deliveries.map((delivery) => (
            <div
              key={delivery.id}
              className={`relative rounded-xl p-4 sm:p-6 shadow-lg transition-transform transform duration-300 hover:scale-105 hover:shadow-2xl ${delivery.status === "canceled"
                ? "bg-red-50"
                : "bg-gradient-to-br from-purple-200 via-bgBody to-pink-100"
                }`}
            >
              <span
                className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold ${statusColorMap[delivery.status]}`}
              >
                {(delivery.status === DELIVERY_STATUS.REQUESTED || delivery.status === DELIVERY_STATUS.FINDING) && delivery.scheduled_at ? (
                  <> Scheduled to start {formatDistanceToNow(new Date(delivery.scheduled_at), { addSuffix: true })} </>
                ) : capitalizeWords(delivery.status)}
              </span>


              {delivery.payment_status == 1 && (
                <span className="absolute bottom-4 left-4 px-3 py-1 rounded-full text-xs font-bold bg-green-200 text-green-800">
                  Paid
                </span>
              )}

              {delivery.pets && delivery.pets.length > 0 && (
                <div className="flex -space-x-3 mb-4">
                  {delivery.pets.slice(0, 3).map((pet, index) => (
                    <img
                      key={index}
                      src={pet.primary_image_url}
                      alt={pet.name}
                      className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover shadow-lg border-2 border-white"
                      style={{ zIndex: delivery.pets.length - index }}
                    />
                  ))}
                  {delivery.pets.length > 3 && (
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gray-300 flex items-center justify-center text-sm font-bold text-white border-2 border-white shadow-lg">
                      +{delivery.pets.length - 3}
                    </div>
                  )}
                </div>
              )}

              {/* <div className="flex items-center space-x-2 text-gray-800 mb-2">
                <FontAwesomeIcon icon={faDollarSign} className="text-primary" />
                <span className="font-semibold">
                  <span className="text-tblue">Charge:</span> {delivery.charges.total} {CURRENCY_CODE}
                </span>
              </div> */}

              <div className="flex items-center space-x-2 text-gray-800 mb-2">
                <FontAwesomeIcon icon={faMoneyBillAlt} className="text-primary" />
                <span className="font-semibold">
                  <span className="text-tblue">Payment Method:</span>{" "}


                  {delivery.payment_method ? (
                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-white text-primary">
                      {delivery.payment_method}
                    </span>
                  ) : (
                    delivery.status === DELIVERY_STATUS.CANCELLED ? (
                      <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-200 text-red-600">
                        Not Selected
                      </span>
                    ) : (
                      <button
                        onClick={() => handleDeliveryPaymentMethod(delivery)}
                        className="text-white text-xs p-2 px-3 rounded-lg bg-red-600 shadow-md transition-all transform hover:scale-105 hover:shadow-lg active:scale-100"
                        title="Select Payment Method"
                      >
                        <FontAwesomeIcon icon={faExclamationCircle} className="text-yellow-300" />
                        <span className="ms-2 font-semibold">Select Payment Method</span>
                      </button>
                    )
                  )}
                </span>
              </div>

              <div className="space-y-2 mt-4 text-sm">
                {/* <div className="flex items-center space-x-2 text-gray-800">
                  <FontAwesomeIcon icon={faPaw} className="text-primary" />
                  <span className="font-semibold">
                    <span className="text-tblue">Rider:</span>{" "}
                    {delivery.rider ? delivery.rider.name : "Unassigned"}
                  </span>
                </div> */}
                <div className="flex items-center space-x-2 text-gray-500">
                  <FontAwesomeIcon icon={faClock} className="text-primary" />
                  <span className="font-semibold text-tblue">
                    {formatDistanceToNow(new Date(delivery.created_at), { addSuffix: true })}
                  </span>
                </div>
              </div>

              <div className="flex justify-end gap-4 mt-6">
                <button
                  onClick={() => handleShow(delivery)}
                  className="text-blue-500 hover:text-blue-700 transition-transform transform hover:scale-110"
                  title="View Delivery Details"
                >
                  <FontAwesomeIcon icon={faEye} />
                </button>

                {/* Reorder Button - Show for completed or cancelled deliveries */}
                <button
                  onClick={() => handleReorder(delivery)}
                  className={`transition-transform transform ${limit === 3
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-green-500 hover:text-green-700 hover:scale-110"
                    }`}
                  title={limit === 3 ? "Delivery limit reached (3/3)" : "Reorder Delivery"}
                  disabled={limit === 3}
                >
                  <FontAwesomeIcon icon={faRedo} />
                </button>

                {(delivery.status == DELIVERY_STATUS.REQUESTED ||
                  delivery.status == DELIVERY_STATUS.FINDING) && (
                    <button
                      onClick={() => handleCancelDelivery(delivery)}
                      className="text-red-500 hover:text-red-700 transition-transform transform hover:scale-110"
                      title="Cancel Delivery"
                    >
                      <FontAwesomeIcon icon={faBan} />
                    </button>
                  )}
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center text-gray-500 py-8">
            No deliveries found.
          </div>
        )}
      </div>

      {/* Smart Pagination */}
      <Pagination
        meta={meta}
        onPageChange={(page) => setCurrentPage(page)}
      />

      {isModalOpen && (
        <DeliveryRequest
          item={editDelivery}
          pets={ownerPets}
          tools={tools}
          partnersData={partners}
          onSave={handleSave}
          permission={permission}
          onCancel={() => setIsModalOpen(false)}
          userUpdateContext={updateContext}
        />
      )}

      {isShowModalOpen && (
        <DeliveryDetailModal
          delivery={editDelivery}
          isOpen={isShowModalOpen}
          onClose={handleShowModalClose}
        />
      )}

      {isCancelDeliveryModalOpen && (
        <CancelDelivery
          onCancel={handleCancel}
          isOpen={isCancelDeliveryModalOpen}
          delivery={editDelivery}
          userUpdateContext={updateContext}
        />
      )}

      {isDeliveryPaymentMethodModalOpen && (
        <DeliveryPaymentMethodSelection
          onCancel={handleCancelDeliveryPaymentMethod}
          isOpen={isDeliveryPaymentMethodModalOpen}
          delivery={editDelivery}
        />
      )}
    </div>
  );
};

export default PetOwnerDelivery;


